import { define } from "@chatbotgang/etude/util/define";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import { css, type SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { fakeT } from "@polifonia/utils/react/fakeT";
import { useStatsigClient } from "@statsig/react-bindings";
import { Link, useMatches } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { FeatureFlagTypes } from "@/features/featureFlag";
import { useGetFeatureFlag } from "@/features/featureFlag";

const styles = {
  List: (muiTheme) => ({
    padding: muiTheme.spacing(2, 2),
    display: "flex",
    flexDirection: "column",
    gap: muiTheme.spacing(1),
  }),
  ListItemButton: {
    padding: "4px 12px",
    height: "32px",
    color: theme.colors.neutral090,
    borderRadius: "4px",
    "&.Mui-selected": {
      color: theme.colors.primary,
      backgroundColor: theme.colors.blue020,
    },
    "&.Mui-disabled": {
      color: theme.colors.neutral060,
    },
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const cssLink = css({
  textDecoration: "none",
});

interface DrawerMenuListItemProps {
  path: string;
  label: string;
  isBeta?: boolean;
}

const DrawerMenuListItem: React.FC<DrawerMenuListItemProps> = ({
  path,
  label,
  isBeta,
}) => {
  const matches = useMatches();
  const selected = useMemo(
    () => matches.some((match) => match.pathname.startsWith(path)),
    [matches, path],
  );

  return (
    <Link to={path} css={cssLink}>
      <ListItem disablePadding>
        <ListItemButton
          component="button"
          selected={selected}
          sx={styles.ListItemButton}
        >
          <ListItemText
            primary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {label}
                {isBeta && (
                  <Chip
                    label="Beta"
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: 10,
                      backgroundColor: theme.colors.primary,
                      color: "white",
                      "& .MuiChip-label": { padding: "0 6px" },
                    }}
                  />
                )}
              </Box>
            }
          />
        </ListItemButton>
      </ListItem>
    </Link>
  );
};

const t = fakeT;

const menuItems = {
  applications: {
    path: "/applications",
    labelTranslationKey: t("applications.dataIntegrations.title"),
    labelFallback: "Data & integrations",
  } as const,
  billing: {
    path: "/billing",
    labelTranslationKey: t("billing.usage.title"),
    labelFallback: "Billing & usage",
  } as const,
  channels: {
    path: "/channels",
    labelTranslationKey: t("sideMenu.channels"),
  } as const,
  contractCenter: {
    path: "/contract-center",
    labelTranslationKey: t("sideMenu.contractCenter"),
    // Shown until the translations are published, so the item does not read
    // as its own key. Once published, the key resolves and this is unused —
    // no edit needed. zh/ja/th see English in the meantime, which is why the
    // feature stays gated to the internal org until then.
    labelFallback: "Contract Center",
  } as const,
  userManagement: {
    path: "/user-management",
    labelTranslationKey: t("userManagement.productAccess.title"),
    labelFallback: "Product access",
  } as const,
  vibeLab: {
    path: "/vibe-lab",
    labelTranslationKey: "CL Vibe Lab",
    beta: true,
  } as const,
} satisfies Record<
  | "applications"
  | "billing"
  | "channels"
  | "contractCenter"
  | "userManagement"
  | "vibeLab",
  {
    path: string;
    labelTranslationKey: string;
    /** Text to show while `labelTranslationKey` has no published translation. */
    labelFallback?: string;
    beta?: boolean;
  }
>;

const drawerItems = define<
  Array<{
    path: keyof typeof menuItems;
    requireFeatureFlag?: Array<FeatureFlagTypes["ToggleKey"]>;
    requireCheckGate?: Array<string>;
  }>
>([
  {
    path: "applications",
  },
  {
    path: "channels",
  },
  {
    path: "userManagement",
    requireCheckGate: ["user_management"],
  },
  {
    path: "billing",
  },
  {
    path: "contractCenter",
    requireCheckGate: ["enable_contract_signing"],
  },
  {
    path: "vibeLab",
    requireFeatureFlag: ["enable-vibe-lab"],
    requireCheckGate: ["enable_vibe_lab"],
  },
]);

const DrawerMenuList: React.FC = () => {
  const { t } = useTranslation();
  const client = useStatsigClient();
  const getFeatureFlag = useGetFeatureFlag();
  const items = useMemo(() => {
    return drawerItems
      .filter(function checkPermission(route) {
        if (!route.requireFeatureFlag && !route.requireCheckGate) return true;
        return (
          route.requireCheckGate?.some((key) => client.checkGate(key)) ||
          route.requireFeatureFlag?.some(getFeatureFlag)
        );
      })
      .map((route) => {
        const menuItem = menuItems[route.path];
        return {
          path: menuItem.path,
          label:
            "labelFallback" in menuItem
              ? t(menuItem.labelTranslationKey, menuItem.labelFallback)
              : t(menuItem.labelTranslationKey),
          isBeta: "beta" in menuItem ? menuItem.beta : false,
        };
      });
  }, [getFeatureFlag, client, t]);
  return (
    <List sx={styles.List}>
      {items.map((item) => (
        <DrawerMenuListItem
          key={item.path}
          path={item.path}
          label={item.label}
          isBeta={item.isBeta}
        />
      ))}
    </List>
  );
};

export { DrawerMenuList };
